<!-- eslint-disable vue/multi-word-component-names -->
<i18n src="src/locales/common/app-error-boundary.json" />
<script setup lang="ts">
import ErrordIllustration from './illustrations/ErrordIllustration.vue';
import { sendUserToFreshLogin } from '@/@core/services/auth/auth';
import {
  clearAuthState,
  hasExceededMaxRetries,
  resetErrorRetryCount,
} from '@/@core/services/auth/errorRecovery';
import { appReady, type AppErrorKind } from '@/appErrorState';
import { AaButton } from '@aleph-alpha/ds-components-vue';
import { computed, onErrorCaptured, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps<{
  /** Pre-mount fatal error (config or session); mutually exclusive with child render. */
  error?: AppErrorKind | null;
}>();

const capturedError = ref<Error | null>(null);

const showErrorPage = computed(
  () =>
    capturedError.value !== null ||
    props.error === 'config' ||
    props.error === 'session',
);

const showRouterSlot = computed(() => appReady.value && !showErrorPage.value);

const maxRetriesExceeded = computed(() => hasExceededMaxRetries());

const title = computed(() => {
  if (capturedError.value) return t('APP_ERROR_BOUNDARY.runtime-title');
  if (props.error === 'config') return t('APP_ERROR_BOUNDARY.config-title');
  if (props.error === 'session') return t('APP_ERROR_BOUNDARY.session-title');
  return '';
});

const subtitle = computed(() => {
  if (maxRetriesExceeded.value)
    return t('APP_ERROR_BOUNDARY.max-retries-description');
  if (capturedError.value) return t('APP_ERROR_BOUNDARY.runtime-description');
  if (props.error === 'config')
    return t('APP_ERROR_BOUNDARY.config-description');
  if (props.error === 'session')
    return t('APP_ERROR_BOUNDARY.session-description');
  return '';
});

onErrorCaptured((err) => {
  capturedError.value = err instanceof Error ? err : new Error(String(err));
  return false;
});

function reload(): void {
  window.location.reload();
}

async function signInAgain(): Promise<void> {
  await clearAuthState();
  await sendUserToFreshLogin();
}

function startOver(): void {
  resetErrorRetryCount();
  window.location.reload();
}
</script>

<template>
  <main v-if="showErrorPage">
    <div
      class="bg-core-bg-secondary flex h-screen w-screen shrink-0 items-center justify-center gap-8 pb-16"
    >
      <ErrordIllustration height="300" width="300" class="shrink-0" />
      <div class="flex w-3/10 shrink-0 flex-col items-start gap-8">
        <h1 class="text-core-content-primary text-3xl font-bold">
          {{ title }}
        </h1>
        <p class="text-core-content-secondary">
          {{ subtitle }}
        </p>
        <div
          v-if="capturedError"
          class="w-full max-w-2xl overflow-auto rounded-md border border-gray-200 bg-gray-50 p-3 text-left font-mono text-xs leading-snug whitespace-pre-wrap text-gray-800"
          aria-label="Error details"
        >
          {{ capturedError.message }}
        </div>
        <template v-if="maxRetriesExceeded">
          <AaButton variant="secondary" size="medium" @click="startOver">
            {{ t('APP_ERROR_BOUNDARY.start-over') }}
          </AaButton>
        </template>
        <template v-else>
          <AaButton
            v-if="props.error === 'session'"
            variant="primary"
            size="medium"
            @click="signInAgain"
          >
            {{ t('APP_ERROR_BOUNDARY.session-sign-in-again') }}
          </AaButton>
          <AaButton
            v-if="props.error === 'config' || capturedError"
            variant="primary"
            size="medium"
            @click="reload"
          >
            {{ t('APP_ERROR_BOUNDARY.reload-page') }}
          </AaButton>
        </template>
      </div>
    </div>
  </main>
  <slot v-else-if="showRouterSlot" />
</template>
